import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${({ theme }) => theme?.colors?.background || 'var(--gray-50)'};
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    font-size: 16px;
    line-height: 1.6;
    transition: background-color 0.3s ease, color 0.3s ease;
    
    /* Theme-aware gray color overrides for dark mode */
    --gray-900: ${({ theme }) => theme?.mode === 'dark' ? '#ffffff' : '#111827'};
    --gray-800: ${({ theme }) => theme?.mode === 'dark' ? '#f3f4f6' : '#1f2937'};
    --gray-700: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : '#374151'};
    --gray-600: ${({ theme }) => theme?.mode === 'dark' ? '#d1d5db' : '#4b5563'};
    --gray-500: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#6b7280'};
    --gray-400: ${({ theme }) => theme?.mode === 'dark' ? '#6b7280' : '#9ca3af'};
    --gray-300: ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#d1d5db'};
    --gray-200: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#e5e7eb'};
    --gray-100: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#f3f4f6'};
    --gray-50: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : '#f9fafb'};
    
    /* Theme-aware primary color overrides for dark mode (light shades become darker) */
    --primary-50: ${({ theme }) => theme?.mode === 'dark' ? '#7f1d1d' : '#fef2f2'};
    --primary-100: ${({ theme }) => theme?.mode === 'dark' ? '#991b1b' : '#fee2e2'};
    --primary-200: ${({ theme }) => theme?.mode === 'dark' ? '#b91c1c' : '#fecaca'};
    
    /* Theme-aware semantic color variables */
    --text-primary: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : '#111827'};
    --text-secondary: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#6b7280'};
    --text-muted: ${({ theme }) => theme?.mode === 'dark' ? '#6b7280' : '#9ca3af'};
    --bg-primary: ${({ theme }) => theme?.mode === 'dark' ? '#111827' : '#ffffff'};
    --bg-secondary: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : '#f9fafb'};
    --bg-tertiary: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#f3f4f6'};
    --border-color: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#e5e7eb'};
    --accent-primary: #ef4444;
    --accent-primary-rgb: 239, 68, 68;
  }

  #root {
    min-height: 100vh;
  }

  /* Red Color Scheme Variables */
  :root {
    --primary-50: #fef2f2;
    --primary-100: #fee2e2;
    --primary-200: #fecaca;
    --primary-300: #fca5a5;
    --primary-400: #f87171;
    --primary-500: #ef4444;
    --primary-600: #dc2626;
    --primary-700: #b91c1c;
    --primary-800: #991b1b;
    --primary-900: #7f1d1d;
    
    --gray-50: #f9fafb;
    --gray-100: #f3f4f6;
    --gray-200: #e5e7eb;
    --gray-300: #d1d5db;
    --gray-400: #9ca3af;
    --gray-500: #6b7280;
    --gray-600: #4b5563;
    --gray-700: #374151;
    --gray-800: #1f2937;
    --gray-900: #111827;
    
    /* Semantic Color Variables - Light Mode Defaults */
    --text-primary: #111827;
    --text-secondary: #6b7280;
    --text-muted: #9ca3af;
    --bg-primary: #ffffff;
    --bg-secondary: #f9fafb;
    --bg-tertiary: #f3f4f6;
    --border-color: #e5e7eb;
    --accent-primary: #ef4444;
    --accent-primary-rgb: 239, 68, 68;
    
    /* Success Colors */
    --success-50: #f0fdf4;
    --success-100: #dcfce7;
    --success-500: #22c55e;
    --success-600: #16a34a;
    --success-700: #15803d;
    
    /* Warning Colors */
    --warning-50: #fefce8;
    --warning-100: #fef3c7;
    --warning-500: #eab308;
    --warning-600: #ca8a04;
    
    /* Info Colors - Updated to use primary red scheme */
    --info-50: #fef2f2;
    --info-100: #fee2e2;
    --info-500: var(--primary-600);
    --info-600: var(--primary-700);
    
    /* Typography Scale */
    --font-size-xs: 0.75rem;    /* 12px */
    --font-size-sm: 0.875rem;   /* 14px */
    --font-size-base: 1rem;     /* 16px */
    --font-size-lg: 1.125rem;   /* 18px */
    --font-size-xl: 1.25rem;    /* 20px */
    --font-size-2xl: 1.5rem;    /* 24px */
    --font-size-3xl: 1.875rem;  /* 30px */
    --font-size-4xl: 2.25rem;   /* 36px */
    --font-size-5xl: 3rem;      /* 48px */
    
    /* Line Heights */
    --leading-tight: 1.25;
    --leading-snug: 1.375;
    --leading-normal: 1.5;
    --leading-relaxed: 1.625;
    --leading-loose: 2;
    
    /* Font Weights */
    --font-light: 300;
    --font-normal: 400;
    --font-medium: 500;
    --font-semibold: 600;
    --font-bold: 700;
    --font-extrabold: 800;
    
    /* Spacing Scale */
    --space-1: 0.25rem;   /* 4px */
    --space-2: 0.5rem;    /* 8px */
    --space-3: 0.75rem;   /* 12px */
    --space-4: 1rem;      /* 16px */
    --space-5: 1.25rem;   /* 20px */
    --space-6: 1.5rem;    /* 24px */
    --space-8: 2rem;      /* 32px */
    --space-10: 2.5rem;   /* 40px */
    --space-12: 3rem;     /* 48px */
    --space-16: 4rem;     /* 64px */
    
    /* Border Radius */
    --radius-sm: 0.5rem;    /* 8px */
    --radius-md: 0.5rem;    /* 8px */
    --radius-lg: 0.5rem;    /* 8px */
    --radius-xl: 0.5rem;    /* 8px */
    --radius-2xl: 0.5rem;   /* 8px */
    --radius-full: 9999px;
    
    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  /* Typography Styles */
  h1, h2, h3, h4, h5, h6 {
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    font-weight: var(--font-bold);
    line-height: var(--leading-tight);
    margin: 0 0 var(--space-4) 0;
  }

  h1 {
    font-size: var(--font-size-4xl);
    font-weight: var(--font-extrabold);
    margin-bottom: var(--space-6);
  }

  h2 {
    font-size: var(--font-size-3xl);
    margin-bottom: var(--space-5);
  }

  h3 {
    font-size: var(--font-size-2xl);
    margin-bottom: var(--space-4);
  }

  h4 {
    font-size: var(--font-size-xl);
    margin-bottom: var(--space-3);
  }

  h5 {
    font-size: var(--font-size-lg);
    margin-bottom: var(--space-3);
  }

  h6 {
    font-size: var(--font-size-base);
    margin-bottom: var(--space-2);
    font-weight: var(--font-semibold);
  }

  p {
    color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-700)'};
    margin-bottom: var(--space-4);
    line-height: var(--leading-relaxed);
  }

  /* Mobile Typography */
  @media (max-width: 768px) {
    h1 {
      font-size: var(--font-size-3xl);
    }
    
    h2 {
      font-size: var(--font-size-2xl);
    }
    
    h3 {
      font-size: var(--font-size-xl);
    }
  }

  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: var(--gray-100);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--primary-600);
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--primary-700);
  }

  /* Form Styles */
  .form-group {
    margin-bottom: var(--space-3);
  }

  .form-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: var(--font-medium);
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
    margin-bottom: var(--space-1);
  }

  .form-label.required::after {
    content: ' *';
    color: var(--primary-600);
  }

  input[type="text"],
  input[type="email"],
  input[type="password"],
  input[type="number"],
  input[type="tel"],
  input[type="url"],
  input[type="date"],
  input[type="datetime-local"],
  input[type="time"],
  select,
  .form-input:not(textarea),
  .form-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid ${({ theme }) => theme?.colors?.inputBorder || 'var(--gray-300)'};
    border-radius: 5px;
    font-size: var(--font-size-sm);
    line-height: var(--leading-normal);
    transition: all 0.2s ease-in-out;
    background: ${({ theme }) => theme?.colors?.inputBackground || 'white'};
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    height: 36px;

    &::placeholder {
      color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-400)'};
    }

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    &:disabled {
      background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
      color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-500)'};
      cursor: not-allowed;
      border-color: ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
    }

    &.error {
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }
  }

  textarea,
  textarea.form-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid ${({ theme }) => theme?.colors?.inputBorder || 'var(--gray-300)'};
    border-radius: 5px;
    font-size: var(--font-size-sm);
    line-height: var(--leading-normal);
    transition: all 0.2s ease-in-out;
    background: ${({ theme }) => theme?.colors?.inputBackground || 'white'};
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    min-height: 6rem;
    height: auto;
    resize: vertical;

    &::placeholder {
      color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-400)'};
    }

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    &:disabled {
      background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
      color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-500)'};
      cursor: not-allowed;
      border-color: ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
    }

    &.error {
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }
  }

  select,
  .form-select {
    cursor: pointer;
    padding: 8px 36px 8px 12px;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 12px center;
    background-repeat: no-repeat;
    background-size: 1rem;
    appearance: none;
  }

  /* Form control class - commonly used */
  .form-control {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid ${({ theme }) => theme?.colors?.inputBorder || 'var(--gray-300)'};
    border-radius: 5px;
    font-size: var(--font-size-sm);
    line-height: var(--leading-normal);
    transition: all 0.2s ease-in-out;
    background: ${({ theme }) => theme?.colors?.inputBackground || 'white'};
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    height: 36px;

    &::placeholder {
      color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-400)'};
    }

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    &:disabled {
      background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
      color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-500)'};
      cursor: not-allowed;
    }
  }

  select.form-control {
    cursor: pointer;
    padding: 8px 36px 8px 12px;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 12px center;
    background-repeat: no-repeat;
    background-size: 1rem;
    appearance: none;
  }

  .form-control-sm {
    padding: 0.375rem 0.5rem;
    height: 30px;
    font-size: var(--font-size-xs);
  }

  .form-error {
    margin-top: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--primary-600);
    font-weight: var(--font-medium);
  }

  .form-help {
    margin-top: var(--space-1);
    font-size: var(--font-size-xs);
    color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-500)'};
  }

  /* Checkbox and Radio Styles */
  .checkbox-group,
  .radio-group {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
  }

  input[type="checkbox"],
  input[type="radio"] {
    width: auto;
    margin: 0;
  }

  /* Button Styles */
  .btn,
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    font-size: var(--font-size-sm);
    font-weight: var(--font-medium);
    line-height: 1;
    border-radius: 5px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    text-decoration: none;
    gap: var(--space-2);
    height: 36px;
    white-space: nowrap;
    user-select: none;
    outline: none;

    &:focus {
      outline: 2px solid var(--primary-600);
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* Primary Button */
    &.btn-primary {
      background-color: var(--primary-600);
      color: white;
      border-color: var(--primary-600);

      &:hover:not(:disabled) {
        background-color: var(--primary-700);
        border-color: var(--primary-700);
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    }

    /* Secondary Button */
    &.btn-secondary {
      background-color: ${({ theme }) => theme?.colors?.cardBackground || 'white'};
      color: var(--primary-600);
      border-color: var(--primary-600);

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme?.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'var(--primary-50)'};
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }
    }

    /* Success Button */
    &.btn-success {
      background-color: var(--success-600);
      color: white;
      border-color: var(--success-600);

      &:hover:not(:disabled) {
        background-color: var(--success-700);
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }
    }

    /* Danger Button */
    &.btn-danger {
      background-color: var(--primary-600);
      color: white;
      border-color: var(--primary-600);

      &:hover:not(:disabled) {
        background-color: var(--primary-700);
        transform: translateY(-1px);
        box-shadow: var(--shadow-md);
      }
    }

    /* Ghost Button */
    &.btn-ghost {
      background-color: transparent;
      color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
      border-color: transparent;

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-100)'};
        color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
      }
    }

    /* Outline Button */
    &.btn-outline {
      background-color: transparent;
      color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
      border-color: ${({ theme }) => theme?.colors?.border || 'var(--gray-300)'};

      &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
        border-color: ${({ theme }) => theme?.colors?.borderLight || 'var(--gray-400)'};
      }
    }

    /* Link Button - for breadcrumbs and inline text links */
    &.btn-link {
      background-color: transparent;
      color: var(--primary-600);
      border-color: transparent;
      padding: 0;
      min-height: auto;
      font-weight: var(--font-medium);
      text-decoration: none;

      &:hover:not(:disabled) {
        color: var(--primary-700);
        text-decoration: underline;
        background-color: transparent;
      }

      &:focus {
        outline: none;
        text-decoration: underline;
      }
    }

    /* Button Sizes */
    &.btn-xs {
      padding: 0.25rem 0.5rem;
      font-size: var(--font-size-xs);
      min-height: 1.75rem;
    }

    &.btn-sm {
      padding: 0.35rem 1rem;
      font-size: var(--font-size-sm);
      min-height: 2rem;
    }

    &.btn-lg {
      padding: 0.5rem 1.5rem;
      font-size: var(--font-size-base);
      min-height: 3rem;
    }

    &.btn-xl {
      padding: 0.75rem 2rem;
      font-size: var(--font-size-lg);
      min-height: 3.5rem;
    }

    /* Full Width */
    &.btn-block {
      width: 100%;
    }

    /* Icon Only */
    &.btn-icon {
      padding: var(--space-2);
      min-width: 2.5rem;
      
      &.btn-sm {
        padding: var(--space-1);
        min-width: 2rem;
      }
      
      &.btn-lg {
        padding: var(--space-3);
        min-width: 3rem;
      }
    }
  }

  /* Table Styles */
  .table-container {
    overflow-x: auto;
    background: ${({ theme }) => theme?.colors?.cardBackground || 'white'};
    border-radius: 8px;
    box-shadow: var(--shadow-sm);
    border: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--font-size-sm);

    th {
      background-color: ${({ theme }) => theme?.colors?.tableHeader || 'var(--gray-50)'};
      color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
      font-weight: var(--font-semibold);
      text-align: left;
      padding: var(--space-2) var(--space-4);
      border-bottom: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      white-space: nowrap;

      &:first-child {
        padding-left: var(--space-4);
      }

      &:last-child {
        padding-right: var(--space-4);
      }
    }

    td {
      padding: var(--space-2) var(--space-4);
      border-bottom: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
      color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
      vertical-align: middle;
      background-color: ${({ theme }) => theme?.colors?.tableRow || 'transparent'};

      &:first-child {
        padding-left: var(--space-4);
      }

      &:last-child {
        padding-right: var(--space-4);
        text-align: right;
      }
    }

    tbody tr {
      transition: background-color 0.2s ease-in-out;
      background-color: ${({ theme }) => theme?.colors?.tableRow || 'transparent'};

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.tableRowHover || 'var(--gray-50)'};
      }

      &:last-child td {
        border-bottom: none;
      }
    }

    &.table-sm {
      th, td {
        padding: var(--space-3);
      }
    }

    &.table-lg {
      th, td {
        padding: var(--space-5);
      }
    }
  }

  /* Card Styles */
  .card {
    background: ${({ theme }) => theme?.colors?.cardBackground || 'white'};
    border-radius: 8px;
    padding: var(--space-4);
    box-shadow: var(--shadow-sm);
    border: 1px solid ${({ theme }) => theme?.colors?.cardBorder || 'var(--gray-200)'};
    transition: all 0.2s ease-in-out;

    &:hover {
      box-shadow: var(--shadow-md);
    }

    &.card-sm {
      padding: var(--space-3);
      border-radius: 8px;
    }

    &.card-lg {
      padding: var(--space-8);
      border-radius: var(--radius-2xl);
    }
  }

  .card-header {
    margin-bottom: var(--space-4);
    padding-bottom: var(--space-3);
    border-bottom: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};

    &:last-child {
      margin-bottom: 0;
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .card-title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-semibold);
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    margin: 0 0 var(--space-1) 0;
  }

  .card-subtitle {
    font-size: var(--font-size-sm);
    color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
    margin: 0;
  }

  .card-body {
    /* Default body styling */
  }

  .card-footer {
    margin-top: var(--space-4);
    padding-top: var(--space-3);
    border-top: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);

    &:first-child {
      margin-top: 0;
      border-top: none;
      padding-top: 0;
    }
  }

  /* Page Container & Header Styles */
  .page-container {
    /* No extra styling - inherits from Layout */
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--space-6);
    padding-bottom: var(--space-6);
    border-bottom: 3px solid #EF4444;

    h1 {
      color: var(--text-primary);
      font-size: 1.5rem;
      font-weight: bold;
      margin: 0 0 0.25rem 0;
    }

    p {
      color: var(--text-secondary);
      margin: 0;
      font-size: 0.875rem;
    }
  }

  /* Stats Grid */
  .stats-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-4);

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .stats-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-4);

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  /* Stat Card */
  .stat-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: var(--space-4);
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: var(--shadow-md);
    }

    .stat-icon {
      font-size: 1.5rem;
      flex-shrink: 0;
    }

    .stat-content {
      flex: 1;
      min-width: 0;
    }

    .stat-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--text-primary);
      line-height: 1.2;
    }

    .stat-label {
      font-size: 0.875rem;
      color: var(--text-secondary);
      margin-top: 0.25rem;
    }

    .stat-change, .stat-trend {
      font-size: 0.75rem;
      margin-top: 0.25rem;

      &.positive, &.success {
        color: #10b981;
      }

      &.negative, &.danger {
        color: #ef4444;
      }
    }
  }

  /* Inner Card (for nested cards) */
  .inner-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: var(--space-4);
  }

  /* Grid Layouts */
  .grid-1-col {
    display: grid;
    grid-template-columns: 1fr;
  }

  .grid-2-col {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .grid-3-col {
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .grid-4-col {
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .col-span-2 {
    grid-column: span 2;

    @media (max-width: 768px) {
      grid-column: span 1;
    }
  }

  /* Tabs */
  .tabs {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--border-color);
    margin-bottom: var(--space-4);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .tab {
    padding: var(--space-3) var(--space-4);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-medium);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;

    &:hover {
      color: var(--text-primary);
      background: var(--bg-secondary);
    }

    &.active {
      color: var(--accent-primary);
      border-bottom-color: var(--accent-primary);
    }
  }

  /* Platform Tabs (for Paid Ads) */
  .platform-tabs {
    display: flex;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
  }

  .platform-tab {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: var(--bg-primary);
    border: 2px solid var(--border-color);
    border-radius: 8px;
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-medium);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--text-muted);
    }

    &.active {
      color: var(--text-primary);
    }

    .tab-icon {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: white;
    }
  }

  /* Campaign Card */
  .campaign-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: var(--space-4);
    margin-bottom: var(--space-4);

    .campaign-header {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      margin-bottom: var(--space-4);
    }

    .campaign-platform {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
      color: white;
    }

    .campaign-info {
      flex: 1;
    }

    .campaign-name {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 0.25rem 0;
    }

    .campaign-type {
      font-size: 0.75rem;
      color: var(--text-secondary);
      text-transform: capitalize;
    }

    .campaign-status {
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 500;
      color: white;
      text-transform: capitalize;
    }

    .campaign-metrics {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: var(--space-2);
      margin-bottom: var(--space-4);

      @media (max-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
      }

      @media (max-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    .metric {
      text-align: center;
      padding: var(--space-2);
      background: var(--bg-secondary);
      border-radius: 6px;

      .metric-value {
        font-size: 1rem;
        font-weight: 600;
        color: var(--text-primary);
        display: block;
      }

      .metric-label {
        font-size: 0.75rem;
        color: var(--text-secondary);
      }
    }

    .campaign-budget {
      margin-bottom: var(--space-4);

      .budget-info {
        display: flex;
        justify-content: space-between;
        font-size: 0.875rem;
        color: var(--text-secondary);
        margin-bottom: 0.5rem;
      }
    }

    .campaign-actions {
      display: flex;
      gap: var(--space-2);
      flex-wrap: wrap;
    }
  }

  /* Progress Bar */
  .progress-bar {
    height: 8px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;

    .progress-fill {
      height: 100%;
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }

  /* Event Card */
  .event-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: var(--space-4);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: var(--shadow-md);
    }

    .event-header {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      margin-bottom: var(--space-3);
    }

    .event-icon {
      font-size: 2rem;
    }

    .event-info {
      flex: 1;
    }

    .event-name {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 0.25rem 0;
    }

    .event-date {
      font-size: 0.75rem;
      color: var(--text-secondary);
    }

    .event-stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: var(--space-2);
      margin-bottom: var(--space-3);

      .stat {
        text-align: center;
        padding: var(--space-2);
        background: var(--bg-secondary);
        border-radius: 6px;

        .stat-value {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary);
          display: block;
        }

        .stat-label {
          font-size: 0.625rem;
          color: var(--text-secondary);
        }
      }
    }

    .event-actions {
      display: flex;
      gap: var(--space-2);
    }
  }

  /* Events Grid */
  .events-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--space-4);
  }

  /* Template Card */
  .template-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: var(--space-4);
    text-align: center;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: var(--shadow-md);
      transform: translateY(-2px);
    }

    .template-icon {
      font-size: 3rem;
      margin-bottom: var(--space-3);
    }

    .template-name {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 0.5rem 0;
    }

    .template-description {
      font-size: 0.875rem;
      color: var(--text-secondary);
      margin: 0 0 var(--space-3) 0;
    }
  }

  /* Templates Grid */
  .templates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: var(--space-4);
  }

  /* Calendar Styles */
  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    margin-bottom: var(--space-4);

    h2 {
      margin: 0;
      font-size: 1.5rem;
      min-width: 100px;
      text-align: center;
    }
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-4);

    @media (max-width: 1024px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  .calendar-month {
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: var(--space-3);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: var(--shadow-md);
    }

    &.current-month {
      border-color: var(--accent-primary);
      box-shadow: 0 0 0 1px var(--accent-primary);
    }

    .month-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--space-2);

      .month-name {
        font-weight: 600;
        color: var(--text-primary);
      }

      .event-count {
        background: var(--accent-primary);
        color: white;
        font-size: 0.75rem;
        padding: 0.125rem 0.5rem;
        border-radius: 9999px;
      }
    }

    .month-events {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .month-event {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .more-events {
      font-size: 0.75rem;
      color: var(--text-secondary);
      text-align: center;
      padding-top: 0.25rem;
    }
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: var(--space-8);
    color: var(--text-secondary);

    p {
      margin: var(--space-2) 0;
    }
  }

  /* Campaigns List */
  .campaigns-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  /* Analytics Grid */
  .analytics-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }
  }

  /* Insights */
  .insights-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-3);

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .insight-card {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--bg-secondary);
    border-radius: 8px;

    .insight-icon {
      font-size: 1.5rem;
    }

    .insight-content {
      flex: 1;
    }

    .insight-value {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--text-primary);
      display: block;
    }

    .insight-label {
      font-size: 0.75rem;
      color: var(--text-secondary);
    }
  }

  /* Chart Container */
  .chart-container, .chart-card {
    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--space-4);

      h3 {
        margin: 0;
      }
    }

    .chart-legend {
      display: flex;
      gap: var(--space-4);
      font-size: 0.875rem;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        color: var(--text-secondary);

        &.revenue { color: var(--accent-primary); }
        &.orders { color: #3b82f6; }
      }
    }
  }

  /* Filters Bar */
  .filters-bar {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    flex-wrap: wrap;

    .search-box {
      position: relative;
      flex: 1;
      min-width: 200px;

      input {
        width: 100%;
        padding: var(--space-2) var(--space-4);
        padding-left: var(--space-10);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        background: var(--bg-primary);
        color: var(--text-primary);
        font-size: 0.875rem;

        &::placeholder {
          color: var(--text-muted);
        }
      }

      .search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .filter-select {
      padding: var(--space-2) var(--space-4);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      background: var(--bg-primary);
      color: var(--text-primary);
      font-size: 0.875rem;
      cursor: pointer;
    }
  }

  /* Breadcrumb Navigation Styles */
  .breadcrumb {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    font-size: var(--font-size-sm);
    flex-wrap: wrap;
  }

  .breadcrumb-link {
    background: none;
    border: none;
    color: var(--primary-500);
    cursor: pointer;
    font-size: var(--font-size-sm);
    font-weight: var(--font-medium);
    padding: 0;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: var(--primary-600);
      text-decoration: underline;
    }
  }

  .breadcrumb-separator {
    color: ${({ theme }) => theme?.colors?.textMuted || 'var(--gray-400)'};
    margin: 0 var(--space-1);
  }

  .breadcrumb-current {
    color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
    font-size: var(--font-size-sm);
  }

  /* Badge Styles */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: var(--space-1) var(--space-3);
    font-size: var(--font-size-xs);
    font-weight: var(--font-medium);
    border-radius: var(--radius-full);
    text-transform: uppercase;
    letter-spacing: 0.025em;
    line-height: 1;

    &.badge-primary {
      background-color: ${({ theme }) => theme?.mode === 'dark' ? '#7f1d1d' : '#fee2e2'};
      color: ${({ theme }) => theme?.mode === 'dark' ? '#fecaca' : '#991b1b'};
    }

    &.badge-success {
      background-color: ${({ theme }) => theme?.mode === 'dark' ? '#14532d' : '#dcfce7'};
      color: ${({ theme }) => theme?.mode === 'dark' ? '#86efac' : '#15803d'};
    }

    &.badge-danger {
      background-color: ${({ theme }) => theme?.mode === 'dark' ? '#7f1d1d' : '#fee2e2'};
      color: ${({ theme }) => theme?.mode === 'dark' ? '#fecaca' : '#991b1b'};
    }

    &.badge-warning {
      background-color: ${({ theme }) => theme?.mode === 'dark' ? '#713f12' : '#fef3c7'};
      color: ${({ theme }) => theme?.mode === 'dark' ? '#fde047' : '#ca8a04'};
    }

    &.badge-info {
      background-color: ${({ theme }) => theme?.mode === 'dark' ? '#1e3a5f' : '#dbeafe'};
      color: ${({ theme }) => theme?.mode === 'dark' ? '#93c5fd' : '#2563eb'};
    }

    &.badge-gray, &.badge-secondary {
      background-color: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#f3f4f6'};
      color: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : '#374151'};
    }

    &.badge-outline {
      background-color: transparent;
      border: 1px solid currentColor;
    }

    &.badge-lg {
      padding: var(--space-2) var(--space-4);
      font-size: var(--font-size-sm);
    }
  }

  /* Loading Animation */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  /* Responsive Utilities */
  .container {
    max-width: 1560px;
    margin: 0 auto;
    padding: 0 var(--space-4);
    width: 100%;
  }

  /* Form Container - use for form pages */
  .form-container,
  .modal-body,
  form {
    max-width: 1560px;
  }

  /* Layout Utilities */
  .flex {
    display: flex;
  }

  .inline-flex {
    display: inline-flex;
  }

  .grid {
    display: grid;
  }

  .block {
    display: block;
  }

  .inline-block {
    display: inline-block;
  }

  .hidden {
    display: none;
  }

  /* Flexbox Utilities */
  .flex-col {
    flex-direction: column;
  }

  .flex-row {
    flex-direction: row;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .items-start {
    align-items: flex-start;
  }

  .items-center {
    align-items: center;
  }

  .items-end {
    align-items: flex-end;
  }

  .items-stretch {
    align-items: stretch;
  }

  .justify-start {
    justify-content: flex-start;
  }

  .justify-center {
    justify-content: center;
  }

  .justify-end {
    justify-content: flex-end;
  }

  .justify-between {
    justify-content: space-between;
  }

  .justify-around {
    justify-content: space-around;
  }

  .flex-1 {
    flex: 1 1 0%;
  }

  .flex-auto {
    flex: 1 1 auto;
  }

  .flex-none {
    flex: none;
  }

  /* Gap Utilities */
  .gap-1 { gap: var(--space-1); }
  .gap-2 { gap: var(--space-2); }
  .gap-3 { gap: var(--space-3); }
  .gap-4 { gap: var(--space-4); }
  .gap-5 { gap: var(--space-5); }
  .gap-6 { gap: var(--space-6); }
  .gap-8 { gap: var(--space-8); }

  /* Grid Utilities */
  .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
  .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .grid-cols-auto { grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); }

  /* Spacing Utilities */
  .m-0 { margin: 0; }
  .m-1 { margin: var(--space-1); }
  .m-2 { margin: var(--space-2); }
  .m-3 { margin: var(--space-3); }
  .m-4 { margin: var(--space-4); }
  .m-5 { margin: var(--space-5); }
  .m-6 { margin: var(--space-6); }
  .m-8 { margin: var(--space-8); }

  .mt-0 { margin-top: 0; }
  .mt-1 { margin-top: var(--space-1); }
  .mt-2 { margin-top: var(--space-2); }
  .mt-3 { margin-top: var(--space-3); }
  .mt-4 { margin-top: var(--space-4); }
  .mt-5 { margin-top: var(--space-5); }
  .mt-6 { margin-top: var(--space-6); }
  .mt-8 { margin-top: var(--space-8); }

  .mb-0 { margin-bottom: 0; }
  .mb-1 { margin-bottom: var(--space-1); }
  .mb-2 { margin-bottom: var(--space-2); }
  .mb-3 { margin-bottom: var(--space-3); }
  .mb-4 { margin-bottom: var(--space-4); }
  .mb-5 { margin-bottom: var(--space-5); }
  .mb-6 { margin-bottom: var(--space-6); }
  .mb-8 { margin-bottom: var(--space-8); }

  .ml-0 { margin-left: 0; }
  .ml-1 { margin-left: var(--space-1); }
  .ml-2 { margin-left: var(--space-2); }
  .ml-3 { margin-left: var(--space-3); }
  .ml-4 { margin-left: var(--space-4); }

  .mr-0 { margin-right: 0; }
  .mr-1 { margin-right: var(--space-1); }
  .mr-2 { margin-right: var(--space-2); }
  .mr-3 { margin-right: var(--space-3); }
  .mr-4 { margin-right: var(--space-4); }

  .p-0 { padding: 0; }
  .p-1 { padding: var(--space-1); }
  .p-2 { padding: var(--space-2); }
  .p-3 { padding: var(--space-3); }
  .p-4 { padding: var(--space-4); }
  .p-5 { padding: var(--space-5); }
  .p-6 { padding: var(--space-6); }
  .p-8 { padding: var(--space-8); }

  .pt-0 { padding-top: 0; }
  .pt-1 { padding-top: var(--space-1); }
  .pt-2 { padding-top: var(--space-2); }
  .pt-3 { padding-top: var(--space-3); }
  .pt-4 { padding-top: var(--space-4); }
  .pt-6 { padding-top: var(--space-6); }

  .pb-0 { padding-bottom: 0; }
  .pb-1 { padding-bottom: var(--space-1); }
  .pb-2 { padding-bottom: var(--space-2); }
  .pb-3 { padding-bottom: var(--space-3); }
  .pb-4 { padding-bottom: var(--space-4); }
  .pb-6 { padding-bottom: var(--space-6); }

  .pl-0 { padding-left: 0; }
  .pl-1 { padding-left: var(--space-1); }
  .pl-2 { padding-left: var(--space-2); }
  .pl-3 { padding-left: var(--space-3); }
  .pl-4 { padding-left: var(--space-4); }

  .pr-0 { padding-right: 0; }
  .pr-1 { padding-right: var(--space-1); }
  .pr-2 { padding-right: var(--space-2); }
  .pr-3 { padding-right: var(--space-3); }
  .pr-4 { padding-right: var(--space-4); }

  /* Typography Utilities */
  .text-xs { font-size: var(--font-size-xs); }
  .text-sm { font-size: var(--font-size-sm); }
  .text-base { font-size: var(--font-size-base); }
  .text-lg { font-size: var(--font-size-lg); }
  .text-xl { font-size: var(--font-size-xl); }
  .text-2xl { font-size: var(--font-size-2xl); }
  .text-3xl { font-size: var(--font-size-3xl); }
  .text-4xl { font-size: var(--font-size-4xl); }

  .font-light { font-weight: var(--font-light); }
  .font-normal { font-weight: var(--font-normal); }
  .font-medium { font-weight: var(--font-medium); }
  .font-semibold { font-weight: var(--font-semibold); }
  .font-bold { font-weight: var(--font-bold); }
  .font-extrabold { font-weight: var(--font-extrabold); }

  .text-left { text-align: left; }
  .text-center { text-align: center; }
  .text-right { text-align: right; }

  .uppercase { text-transform: uppercase; }
  .lowercase { text-transform: lowercase; }
  .capitalize { text-transform: capitalize; }

  /* Color Utilities */
  .text-primary { color: var(--primary-600); }
  .text-gray-400 { color: var(--gray-400); }
  .text-gray-500 { color: var(--gray-500); }
  .text-gray-600 { color: var(--gray-600); }
  .text-gray-700 { color: var(--gray-700); }
  .text-gray-900 { color: var(--gray-900); }
  .text-success { color: var(--success-600); }
  .text-danger { color: var(--primary-600); }
  .text-warning { color: var(--warning-600); }
  .text-white { color: white; }

  .bg-white { background-color: white; }
  .bg-gray-50 { background-color: var(--gray-50); }
  .bg-gray-100 { background-color: var(--gray-100); }
  .bg-primary { background-color: var(--primary-600); }
  .bg-success { background-color: var(--success-600); }

  /* Border Utilities */
  .border { border: 1px solid var(--gray-200); }
  .border-t { border-top: 1px solid var(--gray-200); }
  .border-b { border-bottom: 1px solid var(--gray-200); }
  .border-l { border-left: 1px solid var(--gray-200); }
  .border-r { border-right: 1px solid var(--gray-200); }

  .rounded { border-radius: 8px; }
  .rounded-sm { border-radius: var(--radius-sm); }
  .rounded-md { border-radius: var(--radius-md); }
  .rounded-lg { border-radius: 8px; }
  .rounded-xl { border-radius: 8px; }
  .rounded-2xl { border-radius: var(--radius-2xl); }
  .rounded-full { border-radius: var(--radius-full); }

  /* Width & Height Utilities */
  .w-full { width: 100%; }
  .w-auto { width: auto; }
  .h-full { height: 100%; }
  .h-auto { height: auto; }

  /* Position Utilities */
  .relative { position: relative; }
  .absolute { position: absolute; }
  .fixed { position: fixed; }
  .static { position: static; }

  /* Shadow Utilities */
  .shadow-sm { box-shadow: var(--shadow-sm); }
  .shadow-md { box-shadow: var(--shadow-md); }
  .shadow-lg { box-shadow: var(--shadow-lg); }
  .shadow-xl { box-shadow: var(--shadow-xl); }

  /* Overflow Utilities */
  .overflow-hidden { overflow: hidden; }
  .overflow-auto { overflow: auto; }
  .overflow-x-auto { overflow-x: auto; }
  .overflow-y-auto { overflow-y: auto; }

  /* Media Queries */
  @media (max-width: 768px) {
    .container {
      padding: 0 var(--space-3);
    }

    .table {
      font-size: var(--font-size-xs);
    }

    .card {
      padding: var(--space-4);
      border-radius: 8px;
    }

    /* Mobile-specific utilities */
    .md\\:hidden {
      display: none;
    }

    .sm\\:grid-cols-1 {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    .sm\\:text-sm {
      font-size: var(--font-size-sm);
    }

    .sm\\:p-4 {
      padding: var(--space-4);
    }

    .sm\\:gap-3 {
      gap: var(--space-3);
    }
  }

  @media (min-width: 769px) {
    .sm\\:block {
      display: block;
    }

    .md\\:grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .md\\:grid-cols-3 {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .md\\:flex {
      display: flex;
    }

    .md\\:hidden {
      display: none;
    }
  }

  @media (min-width: 1024px) {
    .lg\\:grid-cols-4 {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .lg\\:grid-cols-3 {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .lg\\:flex {
      display: flex;
    }
  }
`;

export default GlobalStyles;